package com.eladfinish.bible.contest.screens.main.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.RadioButton
import androidx.compose.material3.RadioButtonDefaults
import androidx.compose.material3.ShapeDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.colorAccent
import com.eladfinish.bible.contest.common.gender.HebrewGender
import com.eladfinish.bible.contest.gender_preference_error
import com.eladfinish.bible.contest.gender_preference_sentence
import com.eladfinish.bible.contest.gender_preference_title
import com.eladfinish.bible.contest.ui.components.dialogs.CustomTitle
import com.eladfinish.bible.contest.ui.components.dialogs.DialogPositiveButtonInColumn
import com.eladfinish.bible.contest.ui.layout.isShortWideWindow
import com.eladfinish.bible.contest.welcome_female
import com.eladfinish.bible.contest.welcome_male
import dev.icerock.moko.resources.compose.colorResource
import dev.icerock.moko.resources.compose.stringResource
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.time.Duration.Companion.milliseconds

@Composable
fun HebrewGenderDialog(
    onHebrewGenderConfirmed: (HebrewGender) -> Unit,
) {

    var selectedGender by rememberSaveable { mutableStateOf<HebrewGender?>(null) }
    var showErrorMessage by rememberSaveable { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    Dialog(
        onDismissRequest = {},
        properties = DialogProperties(
            dismissOnBackPress = false,
            dismissOnClickOutside = false,
            usePlatformDefaultWidth = true,
        ),
        content = {
            OutlinedCard(
                modifier = Modifier
                    .heightIn(min = if (isShortWideWindow()) 0.dp else 300.dp, max = 600.dp),
                shape = ShapeDefaults.Medium,
                colors = CardDefaults.elevatedCardColors(),
                elevation = CardDefaults.elevatedCardElevation(),
                content = {
                    CustomTitle(
                        text = stringResource(resource = SharedRes.strings.gender_preference_title),
                    )

                    Box {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(Color.White)
                                // This dialog cannot be dismissed: its button must be reachable on any screen.
                                .verticalScroll(rememberScrollState()),
                        ) {
                            Text(
                                text = stringResource(resource = SharedRes.strings.gender_preference_sentence),
                                modifier = Modifier.padding(horizontal = 12.dp),
                                style = MaterialTheme.typography.bodyMedium.copy(
                                    fontSize = 20.sp,
                                ),
                            )

                            Spacer(modifier = Modifier.height(8.dp))

                            GenderRadioButton(
                                selectedGender = selectedGender,
                                hebrewGender = HebrewGender.MALE,
                                onToggleGender = {
                                    selectedGender = HebrewGender.MALE
                                    showErrorMessage = false
                                },
                            )
                            GenderRadioButton(
                                selectedGender = selectedGender,
                                hebrewGender = HebrewGender.FEMALE,
                                onToggleGender = {
                                    selectedGender = HebrewGender.FEMALE
                                    showErrorMessage = false
                                },
                            )

                            selectedGender?.let {
                                Spacer(modifier = Modifier.height(8.dp))
                                Text(
                                    modifier = Modifier.fillMaxWidth(),
                                    text = stringResource(
                                        resource = when (it) {
                                            HebrewGender.MALE -> SharedRes.strings.welcome_male
                                            HebrewGender.FEMALE -> SharedRes.strings.welcome_female
                                        }
                                    ).plus("!"),
                                    style = MaterialTheme.typography.bodyMedium.copy(
                                        fontSize = 20.sp,
                                    ),
                                    textAlign = TextAlign.Center,
                                )
                            }

                            if (showErrorMessage) {
                                Spacer(modifier = Modifier.height(8.dp))
                                Text(
                                    text = stringResource(resource = SharedRes.strings.gender_preference_error),
                                    modifier = Modifier.padding(horizontal = 12.dp),
                                    color = Color.Red,
                                    style = MaterialTheme.typography.bodyMedium.copy(
                                        fontSize = 20.sp,
                                    ),
                                )
                            }

                            Spacer(modifier = Modifier.height(16.dp))

                            DialogPositiveButtonInColumn(
                                onClick = {
                                    selectedGender?.let {
                                        onHebrewGenderConfirmed(it)
                                    } ?: run {
                                        showErrorMessage = false
                                        scope.launch {
                                            delay(200.milliseconds)
                                            showErrorMessage = true
                                        }
                                    }
                                })
                        }
                    }
                },
            )
        }
    )
}

@Composable
private fun GenderRadioButton(
    selectedGender: HebrewGender?,
    hebrewGender: HebrewGender,
    onToggleGender: () -> Unit,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically
    ) {
        RadioButton(
            selected = selectedGender == hebrewGender,
            onClick = { onToggleGender() },
            colors = RadioButtonDefaults.colors(selectedColor = colorResource(SharedRes.colors.colorAccent)),
        )
        Text(
            text = stringResource(resource = hebrewGender.stringResource),
            modifier = Modifier.padding(start = 8.dp)
                .clickable { onToggleGender() },
            style = MaterialTheme.typography.bodyMedium.copy(
                fontSize = 18.sp,
            ),
        )
    }
}
